import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import * as _ from 'lodash';
import { LifeResourceIdentifier, InterviewDisclosureIdentifier, ConditionDisclosureResource, DisclosurePhysicianResource, PrimaryPhysicianResource, PhysicianResource, ListDefinitionResource, ListItemResource, AisRestService, PhysicianResourceIdentifier, CaseResourceIdentifier, DisclosurePhysicianIdentifier, ReflexiveQuestionResource } from '@Components/mras/core';
import { FormControl, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, AbstractControl, ValidatorFn, ValidationErrors } from '@angular/forms';
import { MatRadioChange } from '@angular/material/radio';
import { ScreensInterviewService } from '../../screens-interview.service';
import { SharedModule } from '@Shared/shared.module';
import { MatSelectChange } from '@angular/material/select';

export const lastVisitValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const lastVisitMonth = control.get("lastVisitMonth")?.value;
  const lastVisitYear = control.get("lastVisitYear")?.value;
  const lastVisitDateExplicitlyUnknown = control.get("lastVisitDateExplicitlyUnknown")?.value;

  console.log('Validating last visit date:', { lastVisitMonth, lastVisitYear, lastVisitDateExplicitlyUnknown });
  if (lastVisitDateExplicitlyUnknown === true) {
    return null;
  }
  let isLastVisitInPast = true;
  if (lastVisitMonth && lastVisitYear) {

    if (Number.isNaN(lastVisitMonth) || lastVisitMonth < 1 || lastVisitMonth > 12) {
        return { invalidMonth: true };
    }

    if (Number.isNaN(lastVisitYear) || lastVisitYear < 1950) {
        return { invalidYear: true };
    }
    
    const visitDate = new Date(Number(lastVisitYear), Number(lastVisitMonth) - 1);
    const now = new Date();
    isLastVisitInPast = visitDate <= now;
  }
  if (!isLastVisitInPast) {
    return { futureDate: true };
  }

  return null;
};

@Component({
    selector: 'jhi-physician-capture',
    templateUrl: './physician-capture.component.html',
    styleUrls: ['./physician-capture.component.scss'],
    imports: [SharedModule]
})
export class PhysicianCaptureComponent implements OnInit, OnDestroy {
    @ViewChild('hasMorePhysiciansRadioGroup', { static: false }) hasMorePhysiciansRadioGroup: ElementRef;
    readonly _ = _; // create assignment so lodash can be used in template

    @Input()
        name: string;

    @Input()
        lifeIdentifier: LifeResourceIdentifier;

    @Input()
        primary: boolean;
    @Input()
        summaryMode: boolean;
    @Input()
        disclosureIdentifier: InterviewDisclosureIdentifier;

    @Input()
        disclosure: ConditionDisclosureResource;
    @Output()
        disclosureChange = new EventEmitter<ConditionDisclosureResource>();

    @Input()
        disclosurePhysicians: DisclosurePhysicianResource[];
    @Output()
        disclosurePhysiciansChange = new EventEmitter<DisclosurePhysicianResource[]>();

    @Input()
        primaryPhysician: PrimaryPhysicianResource;
    @Output()
        primaryPhysicianChange = new EventEmitter<PrimaryPhysicianResource>();

    @Input()
        physicians: PhysicianResource[];
    @Output()
        physiciansChange = new EventEmitter<PhysicianResource[]>();

    showHasMorePhysicians = true;
    physiciansValue: PhysicianResource[];
    disclosurePhysiciansValue: DisclosurePhysicianResource[];
    monthYearInputDisabled = false;

    @Input()
        countries: ListDefinitionResource;

    _hasPhysician: boolean;
    set hasPhysician(val: boolean) {
        this._hasPhysician = val;

        if (val === true) {
            this.hasPhysicians = "Yes";
            return;
        }

        if (val === false) {
            this.hasPhysicians = "No";
            return;
        }

        if (!val)  {
            this.hasPhysicians = undefined;
        }
    }
    get hasPhysician() {
        return this._hasPhysician;
    }

    hasPhysicians: string;
    hasPhysicianLabel: string;
    countryStatesMap = new Map<string, ListItemResource[]>();

    physicianForm: UntypedFormGroup;

    constructor(private aisRestService: AisRestService,
            private screensInterviewService: ScreensInterviewService,
        private readonly _fb: UntypedFormBuilder,
        private readonly cdr: ChangeDetectorRef
    ) {}
    ngOnDestroy(): void {
        this.screensInterviewService.removeForm(this.physicianForm);
    }

    get physcianDetails() {
        return this.physicianForm.controls["physcianDetails"] as UntypedFormArray;
    }

    ngOnInit(): void {
        this.physiciansValue = [];
        let hasMorePhysicians = '';
        if (this.disclosure.physicianCaptureComplete) {
            hasMorePhysicians = "No";
        }

        let physicianMostRecordsUuid = '';
        if (this.disclosure.disclosurePhysicians) {
            for (const e of this.disclosure.disclosurePhysicians) {
                const p = this.disclosurePhysicians?.find(f => f.physicianIdentifier.uuid === e.physicianUuid);
                if (p?.primaryForDisclosure === true) {
                    physicianMostRecordsUuid = e.physicianUuid;
                }
            }
        }

            this.physicianForm = this._fb.group({
                hasPhysicians: ['', Validators.required],
                hasMorePhysicians: [hasMorePhysicians],
                physicianMostRecords: [physicianMostRecordsUuid || ''],
                physcianDetails: this._fb.array([]),            
            });

        if (this.disclosure.disclosurePhysicians?.length > 1) {
            this.physicianForm.controls['physicianMostRecords'].addValidators(Validators.required);
            this.physicianForm.controls['physicianMostRecords'].updateValueAndValidity();
        }

        this.screensInterviewService.addForm(this.physicianForm);

        if (this.primary) {
            this.hasPhysicianLabel = 'Do you have a primary care physician or medical center?';
            if (this.primaryPhysician != null) {
                const primaryPhysician = (this.physicians || []).find(e => e.uuid === this.primaryPhysician[0].physicianIdentifier.uuid);
                if (primaryPhysician != null) {
                    this.hasPhysician = true;
                    this.physicianForm.controls['hasPhysicians'].setValue(this.hasPhysicians);
                    this.physiciansValue.push(primaryPhysician);
                    this.fetchStatesForCountry(primaryPhysician.country.code, primaryPhysician);
                }
            }
        } else {
            this.hasPhysicianLabel = `Do you have a primary care physician, or do you go to a health care facility for ${this.disclosure.aliasName}?`;
            this.disclosurePhysiciansValue = (this.disclosure.disclosurePhysicians || [])
                .map(e => {
                    const resource = this.disclosurePhysicians.find(f => f.physicianIdentifier.uuid === e.physicianUuid);
                    const copy = new DisclosurePhysicianResource();
                    return Object.assign(copy, resource);
                })
                .filter(e => e != null);
            this.physiciansValue = (this.disclosure.disclosurePhysicians || [])
                .map(e => this.physicians.find(f => f.uuid === e.physicianUuid))
                .map(e => {
                    const copy = Object.assign(new PhysicianResource(), e);
                    this.fetchStatesForCountry(copy.country.code, copy);
                    return copy;
                })
                .filter(e => e != null);
            this.hasPhysician = this.disclosure.hasPhysicians;
            this.physicianForm.controls['hasPhysicians'].setValue(this.hasPhysicians);
            if (this.hasPhysician !== false && this.physiciansValue.length === 0) {
                this.hasPhysician = undefined;
                this.physicianForm.controls['hasPhysicians'].setValue(this.hasPhysicians);
            }
        }

        const formArray = <UntypedFormArray>this.physicianForm.get('physcianDetails');

        for (const physicianValue of this.physiciansValue) {
              formArray.push(this.createItem(physicianValue));
              // Attach lastVisitValidator to each physician detail form group
              formArray.at(-1).addValidators(lastVisitValidator);
        }
    }

    createItem(physicianValue: PhysicianResource): UntypedFormGroup {

        const resource = this.disclosurePhysicians.find(f => f.physicianIdentifier.uuid === physicianValue.uuid);
        const fg = this._fb.group({
            id: [{value: physicianValue.uuid, disabled: this.summaryMode}],
            name: [{value:physicianValue.name, disabled: this.summaryMode}],
            speciality: [{value:physicianValue.speciality, disabled: this.summaryMode}],
            addressLine1: [{value:physicianValue.addressLine1, disabled: this.summaryMode}],
            addressLine2: [{value:physicianValue.addressLine2, disabled: this.summaryMode}],
            countryCode: [{value:physicianValue.country.code, disabled: this.summaryMode}],
            stateString: [{value:physicianValue.stateString, disabled: this.summaryMode}],
            city: [{value:physicianValue.city, disabled: this.summaryMode}],
            zip: [{value:physicianValue.zip, disabled: this.summaryMode}],
            phone: [{value:physicianValue.phone, disabled: this.summaryMode}],
            email: [{value:physicianValue.email, disabled: this.summaryMode}],
            fax: [{value:physicianValue.fax, disabled: this.summaryMode}],
            lastVisitDateExplicitlyUnknown: [{value:resource?.lastVisitDateExplicitlyUnknown, disabled: this.summaryMode}]
        }, {validators: lastVisitValidator});

        if (physicianValue.state != null && physicianValue.state.code != null) {
            fg.controls['stateString'].setValue(physicianValue.state.code);
        }

        if (resource?.lastVisitDateExplicitlyUnknown === true) {
            fg.addControl('lastVisitMonth', new UntypedFormControl({value: resource?.lastVisitMonth, disabled: true }));
            fg.addControl('lastVisitYear', new UntypedFormControl({value: resource?.lastVisitYear, disabled: true }));
            this.monthYearInputDisabled = true;
        }else{            
            fg.addControl('lastVisitMonth', new UntypedFormControl({value: resource?.lastVisitMonth, disabled: this.summaryMode}));
            fg.controls['lastVisitMonth'].addValidators(Validators.required);
            fg.addControl('lastVisitYear', new UntypedFormControl({value: resource?.lastVisitYear, disabled: this.summaryMode}));
            fg.controls['lastVisitYear'].addValidators(Validators.required);
            this.monthYearInputDisabled = false;
        }
        fg.markAsPristine();

        return fg;
    }

    mapPhysician(idx: number): PhysicianResource {
        const formArray = <UntypedFormArray>this.physicianForm.get('physcianDetails');
        
        const physicianDetail = formArray.controls[idx] as UntypedFormGroup;
        const disclosure = this.disclosurePhysiciansValue[idx];

        disclosure.lastVisitMonth = physicianDetail.controls["lastVisitMonth"].value;        
        disclosure.lastVisitYear = physicianDetail.controls["lastVisitYear"].value;      
        disclosure.lastVisitDateExplicitlyUnknown = physicianDetail.controls["lastVisitDateExplicitlyUnknown"].value;

        if (disclosure.lastVisitDateExplicitlyUnknown === true) {
            disclosure.lastVisitMonth = 0;
            disclosure.lastVisitYear = 0;
        }

        if (physicianDetail.controls["id"].value !== null || physicianDetail.controls["id"].value !== '') {
            const physician = this.physiciansValue[idx];
            
            physician.name= physicianDetail.controls["name"].value;
            physician.speciality= physicianDetail.controls["speciality"].value;
            physician.addressLine1= physicianDetail.controls["addressLine1"].value;
            physician.addressLine2= physicianDetail.controls["addressLine2"].value;
            physician.country= {
                code: physicianDetail.controls["countryCode"].value,
                description: physicianDetail.controls["countryCode"].value,
            };
            physician.stateString= physicianDetail.controls["stateString"].value;
            physician.state = this.countryStatesMap.get(physician.country.code)?.find(e => e.code === physician.stateString) || null;
            physician.city= physicianDetail.controls["city"].value;
            physician.zip= physicianDetail.controls["zip"].value;
            physician.phone= physicianDetail.controls["phone"].value;
            physician.email= physicianDetail.controls["email"].value;
            physician.fax= physicianDetail.controls["fax"].value;

            return physician;
        }else{
            return {
                uuid: physicianDetail.controls["id"].value,
                name: physicianDetail.controls["name"].value,
                speciality: physicianDetail.controls["speciality"].value,
                addressLine1: physicianDetail.controls["addressLine1"].value,
                addressLine2: physicianDetail.controls["addressLine2"].value,
                country: {
                    code: physicianDetail.controls["countryCode"].value,
                    description: physicianDetail.controls["countryCode"].value,
                },
                stateString: physicianDetail.controls["stateString"].value,
                city: physicianDetail.controls["city"].value,
                zip: physicianDetail.controls["zip"].value,
                phone: physicianDetail.controls["phone"].value,
                email: physicianDetail.controls["email"].value,
                fax: physicianDetail.controls["fax"].value,
                _embedded: null,
                _links: null,
                links: null,
                source: null,
                state: null
            };
        }
    }

    submit($index: number) {
        const formArray = <UntypedFormArray>this.physicianForm.get('physcianDetails');
        const physicianDetail = formArray.controls[$index] as UntypedFormGroup;

        physicianDetail.markAllAsTouched();
        physicianDetail.updateValueAndValidity();

        if (physicianDetail.invalid) {            
            return;
        }

        const physicianResource = this.mapPhysician($index);

        if (physicianResource == null) {
            return;
        }

        let caseId;
        if (this.primary) {
            caseId = this.lifeIdentifier.caseId;
        } else {
            caseId = this.disclosureIdentifier.caseId;
        }

        let physician$: Observable<PhysicianResource>;
        const isUpdate = physicianResource.uuid != null;
        if (isUpdate) {
            const identifier = new PhysicianResourceIdentifier();
            identifier.caseId = caseId;
            identifier.uuid = physicianResource.uuid;
            physician$ = this.aisRestService.updatePhysician(identifier, physicianResource);
        } else {
            const identifier = new CaseResourceIdentifier();
            identifier.caseId = caseId;
            physician$ = this.aisRestService.createPhysician(identifier, physicianResource);
        }

        physician$
            .pipe(
                tap(e => {
                    if (this.primary) {
                        this.physiciansValue[0].uuid = e.uuid;
                    } else {
                        this.physiciansValue[$index].uuid = e.uuid;
                    }
                    if (this.physicians) {
                        this.physicians.push(e);
                        this.physiciansChange.emit(this.physicians);
                    }
                }),
                map(e => {
                    const identifier = new PhysicianResourceIdentifier();
                    identifier.uuid = e.uuid;
                    identifier.caseId = caseId;
                    
                    const formArray = <UntypedFormArray>this.physicianForm.get('physcianDetails');
                    const physicianDetail = formArray.controls[$index] as UntypedFormGroup;

                    physicianDetail.controls['id'].setValue(identifier.uuid);

                    return identifier;
                })
            )
            .subscribe(physicianIdentifier => {
                if (this.primary) {
                    const primaryPhysicianResource = new PrimaryPhysicianResource();
                    primaryPhysicianResource.physicianExpected = this.hasPhysician;
                    primaryPhysicianResource.physicianIdentifier = physicianIdentifier;
                    primaryPhysicianResource.lastVisitDateExplicitlyUnknown = true;
                    if (isUpdate) {
                        this.aisRestService.updatePrimaryPhysician(this.lifeIdentifier, primaryPhysicianResource).subscribe();
                    } else {
                        this.aisRestService.createPrimaryPhysician(this.lifeIdentifier, primaryPhysicianResource).subscribe();
                    }
                } else {
                    const resource = this.disclosurePhysiciansValue[$index];
                    resource.physicianIdentifier = physicianIdentifier;
                    if (isUpdate) {
                        const disclosurePhysicianIdentifier = Object.assign(new DisclosurePhysicianIdentifier(), this.disclosureIdentifier);
                        disclosurePhysicianIdentifier.physicianUuid = resource.physicianIdentifier.uuid;

                        this.aisRestService.updateDisclosurePhysician(disclosurePhysicianIdentifier, resource).subscribe(ignoredResp => {
                            this.aisRestService.getDisclosure(this.disclosureIdentifier).subscribe(getDisclosureResp => {
                                Object.keys(this.disclosure).forEach(k => delete this.disclosure[k]);
                                Object.assign(this.disclosure, getDisclosureResp);
                                this.disclosureChange.emit(this.disclosure);

                                const formArray = <UntypedFormArray>this.physicianForm.get('physcianDetails');
                                        
                                const physicianDetail = formArray.controls[$index] as UntypedFormGroup;
                                this.physicianForm.markAsUntouched();
                                this.physicianForm.markAsPristine();
                            });
                        });
                    } else {
                        this.aisRestService.createDisclosurePhysician(this.disclosureIdentifier, resource).subscribe(ignoredResp => {
                            this.aisRestService.getDisclosure(this.disclosureIdentifier).subscribe(getDisclosureResp => {
                                Object.keys(this.disclosure).forEach(k => delete this.disclosure[k]);
                                Object.assign(this.disclosure, getDisclosureResp);
                                this.disclosureChange.emit(this.disclosure);

                                const formArray = <UntypedFormArray>this.physicianForm.get('physcianDetails');
                                        
                                const physicianDetail = formArray.controls[$index] as UntypedFormGroup;
                                this.physicianForm.markAsUntouched();
                                this.physicianForm.markAsPristine();

                                this.showHasMorePhysicians = true;

                                if (this.physicians.length > 1) {
                                    this.physicianForm.controls['physicianMostRecords'].addValidators(Validators.required);
                                    this.physicianForm.controls['physicianMostRecords'].updateValueAndValidity();
                                }
                            });
                        });
                    }
                }
            });
    }

    clear($index: number) {
        if (this.physiciansValue) {
            for (const k of Object.keys(this.physiciansValue[$index])) {
                this.physiciansValue[$index][k] = null;
            }
            this.physiciansValue[$index].country = new ListItemResource();
            this.physiciansValue[$index].uuid = undefined;

            const formArray = <UntypedFormArray>this.physicianForm.get('physcianDetails');
            formArray.controls[$index].reset();
        }
        if (this.disclosurePhysiciansValue) {
            for (const k of Object.keys(this.disclosurePhysiciansValue[$index])) {
                this.disclosurePhysiciansValue[$index][k] = null;
            }
        }
    }


    remove($index: number) {
        if (this.physiciansValue) {
            let caseId;
            if (this.primary) {
                caseId = this.lifeIdentifier.caseId;
            } else {                
                caseId = this.disclosureIdentifier.caseId;
            }

            if ($index === 0 && this.physiciansValue.length === 1 && this.hasPhysician === true) {               
                if (this.disclosure.disclosurePhysicians && this.disclosure.disclosurePhysicians.length > 0) {
                    const physicianDisclosureIdentifier = this.disclosure.disclosurePhysicians[$index];
                    this.aisRestService.deleteDisclosurePhysician(physicianDisclosureIdentifier).subscribe(() => { });
                }
                
                this.clear($index);
                
                return;
            }
            
            const physicianResource = this.physiciansValue[$index];
            const formArray = <UntypedFormArray>this.physicianForm.get('physcianDetails');

            if (physicianResource.uuid) {            
                const identifier = new PhysicianResourceIdentifier();
                identifier.caseId = caseId;
                identifier.uuid = physicianResource.uuid;
                const physicianDisclosureIdentifier = this.disclosure.disclosurePhysicians[$index];

                this.aisRestService.deleteDisclosurePhysician(physicianDisclosureIdentifier).subscribe(() => {});
            }

            this.physiciansValue.splice($index,1);
            formArray.controls.splice($index,1);        

            formArray.updateValueAndValidity();

            if (formArray.controls.length === 0) {
                this.hasPhysician = undefined;
            }

            if (this.physicians.length > 1) {
                this.physicianForm.controls['physicianMostRecords'].removeValidators(Validators.required);
                this.physicianForm.controls['physicianMostRecords'].updateValueAndValidity();
            }

            if (formArray.controls.length === 0) {
                this.physicianForm.controls['hasMorePhysicians'].removeValidators(Validators.required);       
                this.physicianForm.controls['hasMorePhysicians'].updateValueAndValidity();                                      
            }

            this.physicianForm.updateValueAndValidity();
            this.physicianForm.markAsPristine();
        }
        if (this.disclosurePhysiciansValue) {
            for (const k of Object.keys(this.disclosurePhysiciansValue[$index])) {
                this.disclosurePhysiciansValue[$index][k] = null;
            }
        }
        
        this.showHasMorePhysicians = true;
    }

    onRadioChange(event: MatRadioChange) {
        if (event.value.toString() === "No") {
            this.hasPhysicianAnswer(false);
        }
        if (event.value.toString() === "Yes") {
            this.hasPhysicianAnswer(true);
        }
    }


    updatePrimaryPhysician(event: MatRadioChange) 
    {
        const resource = new DisclosurePhysicianResource();        
        resource.primaryForDisclosure = true;

        const physicianDisclosureIdentifier = this.disclosure.disclosurePhysicians.find(x => x.physicianUuid === event.value);        
        this.aisRestService.updateDisclosurePhysician(physicianDisclosureIdentifier, resource).subscribe(ignoredResp => {
            this.aisRestService.getDisclosure(this.disclosureIdentifier).subscribe(getDisclosureResp => {
                for (const k of Object.keys(this.disclosure)) {
                    delete this.disclosure[k];
                }
                Object.assign(this.disclosure, getDisclosureResp);
                this.disclosureChange.emit(this.disclosure);

            });
        });
    }


    addAnotherPhysician(event: MatRadioChange) {
        if (event.value.toString() === "No") {
            const resource = new ConditionDisclosureResource();
            resource.hasPhysicians = true;
            resource.physicianCaptureComplete = true;
            resource._ureMetadata = {
                underwrite: true
            };
            
            if (this.disclosure.questions) {
                resource.questions = this.disclosure.questions.map(e => {
                    const question = new ReflexiveQuestionResource();
                    if (e.answerType === 'PICK_LIST') {
                        question.answerValue = 'true';
                        question.triggeredAliases = e.triggeredAliases;
                    } else {                        
                        question.answerValue = e.answerValue;
                    }
                    return question;
                });
            }
            this.aisRestService.updateDisclosure(this.disclosureIdentifier, resource).subscribe(e => {
                this.hasPhysician = e.hasPhysicians;
                this.physicianForm.controls['hasPhysicians'].setValue(this.hasPhysicians);
            });

            if (this.physiciansValue.length <= 1) {
                this.physicianForm.controls['physicianMostRecords'].removeValidators(Validators.required);
                this.physicianForm.controls['physicianMostRecords'].updateValueAndValidity();
            }
        }
        if (event.value.toString() === "Yes") {
            this.hasPhysicianAnswer(true);
            
            this.showHasMorePhysicians = false;
            const ctrl = this.physicianForm.controls['hasMorePhysicians'];
            ctrl.clearValidators();
            ctrl.reset()
            ctrl.markAsPristine();
            ctrl.markAsUntouched();
            ctrl.updateValueAndValidity();

            // Re-add required validator after a tick
            setTimeout(() => {
                ctrl.setValidators(Validators.required);
                ctrl.updateValueAndValidity();

                this.hasMorePhysiciansRadioGroup?.nativeElement?.blur();
                this.cdr.detectChanges();
            });
        }
    }

    onUnknownChange(event, idx) {
        const formArray = <UntypedFormArray>this.physicianForm.get('physcianDetails');        
        const physicianDetail = formArray.controls[idx] as UntypedFormGroup;        

        if (event.checked) {
            physicianDetail.controls['lastVisitMonth'].clearValidators();
            physicianDetail.controls['lastVisitYear'].clearValidators();

            physicianDetail.controls['lastVisitMonth'].setValue('');
            physicianDetail.controls['lastVisitYear'].setValue('');

            physicianDetail.controls['lastVisitMonth'].disable();
            physicianDetail.controls['lastVisitYear'].disable();

            physicianDetail.controls['lastVisitMonth'].updateValueAndValidity();
            physicianDetail.controls['lastVisitYear'].updateValueAndValidity();
            this.monthYearInputDisabled = true;
        }else{
            physicianDetail.controls['lastVisitMonth'].addValidators(Validators.required);
            physicianDetail.controls['lastVisitYear'].addValidators(Validators.required);            

            physicianDetail.controls['lastVisitMonth'].enable();
            physicianDetail.controls['lastVisitYear'].enable();

            physicianDetail.controls['lastVisitMonth'].updateValueAndValidity();
            physicianDetail.controls['lastVisitYear'].updateValueAndValidity();
            this.monthYearInputDisabled = false;
        }
    }

    hasPhysicianAnswer(val: boolean) {
        this.hasPhysician = val;
        
        if (this.primary && !this.hasPhysician && this.primaryPhysician != null) {
            this.aisRestService.deletePrimaryPhysician(this.lifeIdentifier).subscribe(e => {
                this.primaryPhysician = null;
                this.physiciansValue[0].uuid = null;
                this.primaryPhysicianChange.emit(null);
            });
            return;
        }

        if (!this.primary) {
            const resource = new ConditionDisclosureResource();
            resource.hasPhysicians = this.hasPhysician;

            if (this.disclosure.questions) {
                resource.questions = this.disclosure.questions.map(e => {
                    const question = new ReflexiveQuestionResource();
                    if (e.answerType === 'PICK_LIST') {
                        question.answerValue = 'true';

                        if (e.triggeredAliases?.length === 0) {
                            question.triggeredAliases = [""];
                        }else{
                            question.triggeredAliases = e.triggeredAliases;
                        }

                        
                    } else {                        
                        question.answerValue = e.answerValue;
                    }
                    return question;
                });
            }

            this.aisRestService.updateDisclosure(this.disclosureIdentifier, resource).subscribe(e => {
                this.hasPhysician = e.hasPhysicians;
                this.physicianForm.controls['hasPhysicians'].setValue(this.hasPhysicians);
            });
        }

        if (this.hasPhysician) {
            const physicianResource = new PhysicianResource();
            physicianResource.country = new ListItemResource();
            this.physiciansValue.push(physicianResource);
            const formArray = <UntypedFormArray>this.physicianForm.get('physcianDetails');
            formArray.push(this.createItem(physicianResource));

            if (!this.primary) {
                this.disclosurePhysiciansValue.push(new DisclosurePhysicianResource());
            }

            this.disclosure.physiciansValid = false;
        }

        if (this.hasPhysician === false) {
            let cnt = this.physiciansValue.length;

            while (cnt > 0) {
                cnt--;
                this.remove(cnt);
            }            
        }
    }

    getPhysicians() {
        if (this.physiciansValue.length === 0) {
            const physicianResource = new PhysicianResource();
            physicianResource.country = new ListItemResource();
            this.physiciansValue.push(physicianResource);
        }
        if (!this.primary && this.disclosurePhysiciansValue.length === 0) {
            this.disclosurePhysiciansValue.push(new DisclosurePhysicianResource());
        }
        return this.physiciansValue;
    }

    mapToOptionValueFromListItems(resource: ListItemResource): string {
        return resource.code;
    }

    mapToOptionLabelFromListItems(resource: ListItemResource): string {
        return resource.description;
    }

    countrySelectionChange(event: MatSelectChange, $index: number) {
        const physician = this.physiciansValue[$index];
        const country = event.value;

        this.fetchStatesForCountry(country, physician);
    }

    fetchStatesForCountry(countryCode: string, physician: PhysicianResource) {
        this.aisRestService.getStatesForCountry(this.lifeIdentifier, countryCode).subscribe(e => {
            physician.country.description = null;
            this.countryStatesMap.set(countryCode, e);
            if (e.length === 0) {
                physician.state = null;
            }
            if (e.length > 0 && physician.state == null) {
                physician.state = new ListItemResource();
                physician.stateString = null;
            }
        });
    }
}
